import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

// Wheel delta (px) accumulated over the rail before stepping one category.
// Deliberately high so the rail isn't twitchy — you scroll a bit before it commits.
const RAIL_WHEEL_STEP = 220;
// Fraction of viewport width past which a wheel event counts as "over the rail".
const RAIL_REGION_LEFT_FRACTION = 0.62;
// After the gesture stops, snap to whichever item is closer.
const RAIL_RELEASE_MS = 150;
// Swallow the inertial tail after a snap so one trackpad gesture cannot chain tabs.
const RAIL_INERTIA_LOCK_MS = 340;
const RAIL_IDLE_DELTA = 0;

// Returns a signed progress (-1..1) toward the next/previous category — the rail
// uses it to nudge mid-scroll before the step actually commits at ±1.
export function useChatteaser3RailWheel({
  activeTabRef,
  startTabTransition,
  tabCount,
}: {
  activeTabRef: RefObject<number>;
  startTabTransition: (value: number) => void;
  tabCount: number;
}): number {
  const [scrollProgress, setScrollProgress] = useState(0);
  const accumulatedRef = useRef(0);
  const inertiaLockedUntilRef = useRef(0);

  const reset = useCallback((): void => {
    accumulatedRef.current = 0;
    setScrollProgress(0);
  }, []);

  const releaseToClosest = useCallback((): void => {
    const accumulatedDelta = accumulatedRef.current;
    reset();
    inertiaLockedUntilRef.current = performance.now() + RAIL_INERTIA_LOCK_MS;
    if (accumulatedDelta >= RAIL_WHEEL_STEP / 2 && activeTabRef.current < tabCount - 1) {
      startTabTransition(activeTabRef.current + 1);
    } else if (accumulatedDelta <= -RAIL_WHEEL_STEP / 2 && activeTabRef.current > 0) {
      startTabTransition(activeTabRef.current - 1);
    }
  }, [activeTabRef, reset, startTabTransition, tabCount]);

  useEffect(() => {
    if (scrollProgress === 0) return;

    const releaseTimer = window.setTimeout(releaseToClosest, RAIL_RELEASE_MS);
    return () => window.clearTimeout(releaseTimer);
  }, [releaseToClosest, scrollProgress]);

  useEffect(() => {
    const onRailWheel = (event: WheelEvent): void => {
      if (event.clientX < window.innerWidth * RAIL_REGION_LEFT_FRACTION) return;

      const activeTab = activeTabRef.current;
      const isAtFirstTab = activeTab <= 0;
      const isAtLastTab = activeTab >= tabCount - 1;
      const isBoundaryExit =
        (event.deltaY < RAIL_IDLE_DELTA && isAtFirstTab) ||
        (event.deltaY > RAIL_IDLE_DELTA && isAtLastTab);
      if (isBoundaryExit && accumulatedRef.current === RAIL_IDLE_DELTA) return;
      if (performance.now() < inertiaLockedUntilRef.current) {
        if (isBoundaryExit) return;
        event.preventDefault();
        return;
      }

      let nextAccumulatedDelta = accumulatedRef.current + event.deltaY;
      // Don't accumulate past either end of the list.
      if (isAtLastTab && nextAccumulatedDelta > RAIL_IDLE_DELTA) {
        nextAccumulatedDelta = RAIL_IDLE_DELTA;
      }
      if (isAtFirstTab && nextAccumulatedDelta < RAIL_IDLE_DELTA) {
        nextAccumulatedDelta = RAIL_IDLE_DELTA;
      }
      if (
        nextAccumulatedDelta === RAIL_IDLE_DELTA &&
        accumulatedRef.current === RAIL_IDLE_DELTA
      ) {
        return;
      }
      event.preventDefault();
      nextAccumulatedDelta = Math.max(
        -RAIL_WHEEL_STEP,
        Math.min(RAIL_WHEEL_STEP, nextAccumulatedDelta),
      );
      accumulatedRef.current = nextAccumulatedDelta;

      setScrollProgress(nextAccumulatedDelta / RAIL_WHEEL_STEP);
    };

    window.addEventListener('wheel', onRailWheel, { passive: false });
    return () => window.removeEventListener('wheel', onRailWheel);
  }, [activeTabRef, tabCount]);

  return scrollProgress;
}
